/**
 * Iter-228x-bis — Shared search/filter bar for the 4 expense pages.
 *
 * Filters: category (multi), vendor (substring), date period (preset or custom range).
 * Emits a `value` object: { category: string[], vendor: string, from: "YYYY-MM-DD", to: "YYYY-MM-DD", preset: "month"|"quarter"|"ytd"|"custom"|"all" }
 *
 * Use the helper `applySearchFilters(rows, value)` to filter a row list
 * client-side when the rows are already loaded for a single month.
 * Use the helper `searchRowsToCsv(rows, kind)` to build a CSV blob.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Filter, X, Download } from "lucide-react";

const PRESETS = [
  { id: "month", label: "Ce mois" },
  { id: "quarter", label: "Trimestre" },
  { id: "ytd", label: "Année (YTD)" },
  { id: "custom", label: "Période…" },
  { id: "all", label: "Tout" },
];

const fmtDate = (d) => d.toISOString().slice(0, 10);

export const computePresetRange = (preset) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  if (preset === "month") {
    return { from: fmtDate(new Date(y, m, 1)), to: fmtDate(new Date(y, m + 1, 0)) };
  }
  if (preset === "quarter") {
    const qStart = Math.floor(m / 3) * 3;
    return { from: fmtDate(new Date(y, qStart, 1)), to: fmtDate(new Date(y, qStart + 3, 0)) };
  }
  if (preset === "ytd") {
    return { from: fmtDate(new Date(y, 0, 1)), to: fmtDate(today) };
  }
  if (preset === "all") {
    return { from: "", to: "" };
  }
  return null; // custom — caller manages from/to.
};

export default function ExpenseSearchBar({
  initialPreset = "month",
  categories = [],
  value,
  onChange,
  onExport,
  count = null,
  testidPrefix = "exp-search",
}) {
  const [v, setV] = useState(
    () => value || { preset: initialPreset, category: [], vendor: "", ...(computePresetRange(initialPreset) || { from: "", to: "" }) },
  );

  useEffect(() => { if (value) setV(value); }, [value]);

  const update = (next) => { setV(next); onChange?.(next); };

  const setPreset = (preset) => {
    const range = computePresetRange(preset);
    update({ ...v, preset, ...(range || {}) });
  };

  const toggleCategory = (cat) => {
    const has = v.category.includes(cat);
    update({ ...v, category: has ? v.category.filter((c) => c !== cat) : [...v.category, cat] });
  };

  const reset = () => {
    const range = computePresetRange(initialPreset);
    update({ preset: initialPreset, category: [], vendor: "", ...(range || { from: "", to: "" }) });
  };

  const hasActiveFilter = v.category.length > 0 || v.vendor.trim().length > 0 || v.preset !== initialPreset;

  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 space-y-2" data-testid={testidPrefix}>
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-zinc-500" />
        <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">Filtres</span>
        {PRESETS.map((p) => (
          <button
            key={p.id} type="button"
            onClick={() => setPreset(p.id)}
            data-testid={`${testidPrefix}-preset-${p.id}`}
            className={`px-2 py-0.5 text-xs rounded border ${v.preset === p.id ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"}`}
          >
            {p.label}
          </button>
        ))}
        {v.preset === "custom" && (
          <div className="inline-flex items-center gap-1">
            <input type="date" value={v.from} onChange={(e) => update({ ...v, from: e.target.value })}
              data-testid={`${testidPrefix}-from`}
              className="text-xs px-1.5 py-0.5 border border-zinc-300 rounded font-mono" />
            <span className="text-zinc-500 text-xs">→</span>
            <input type="date" value={v.to} onChange={(e) => update({ ...v, to: e.target.value })}
              data-testid={`${testidPrefix}-to`}
              className="text-xs px-1.5 py-0.5 border border-zinc-300 rounded font-mono" />
          </div>
        )}
        <div className="grow" />
        {count != null && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500" data-testid={`${testidPrefix}-count`}>
            {count} ligne(s)
          </span>
        )}
        {hasActiveFilter && (
          <button type="button" onClick={reset}
            data-testid={`${testidPrefix}-reset`}
            className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
            <X size={12} /> Reset
          </button>
        )}
        {onExport && (
          <button type="button" onClick={onExport}
            data-testid={`${testidPrefix}-export`}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100">
            <Download size={12} /> Export CSV
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={v.vendor}
          onChange={(e) => update({ ...v, vendor: e.target.value })}
          placeholder="Fournisseur (recherche partielle)…"
          data-testid={`${testidPrefix}-vendor`}
          className="text-xs px-2 py-1 border border-zinc-300 rounded w-56"
        />
        <div className="flex flex-wrap gap-1" data-testid={`${testidPrefix}-categories`}>
          {(categories || []).map((c) => (
            <button
              key={c} type="button"
              onClick={() => toggleCategory(c)}
              data-testid={`${testidPrefix}-cat-${c}`}
              className={`px-2 py-0.5 text-[11px] rounded-full border ${v.category.includes(c) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper — client-side filter of a row list using a search value.
export function applySearchFilters(rows, v) {
  if (!v) return rows || [];
  return (rows || []).filter((l) => {
    if (v.from && (l.invoice_date || "") < v.from) return false;
    if (v.to && (l.invoice_date || "") > v.to) return false;
    if (v.category?.length && !v.category.includes(l.category)) return false;
    if (v.vendor && !((l.vendor || "").toLowerCase().includes(v.vendor.trim().toLowerCase()))) return false;
    return true;
  });
}

// Helper — build a CSV blob from a filtered row list.
export function searchRowsToCsv(rows, kind = "mine") {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return s.includes(";") || s.includes("\n") || s.includes('"') ? `"${s}"` : s;
  };
  const fmt = (v) => v == null || v === "" ? "" : (typeof v === "number" ? v.toFixed(2).replace(".", ",") : v);
  const header = kind === "cabinet"
    ? ["Date", "Fournisseur", "N° Facture", "Catégorie", "Description", "Montant", "Devise", "HT", "TVA", "Taux", "TTC", "MAD"]
    : kind === "all"
      ? ["Date", "Source", "Statut", "Utilisateur", "Fournisseur", "N° Facture", "Catégorie", "Description", "Montant", "Devise", "MAD", "Mission"]
      : ["Date", "Statut", "Fournisseur", "N° Facture", "Catégorie", "Description", "Montant", "Devise", "MAD", "Mission", "Utilisateur"];
  const lines = [header.join(";")];
  for (const l of rows || []) {
    if (kind === "cabinet") {
      lines.push([l.invoice_date, l.vendor, l.invoice_number, l.category, l.description, fmt(l.amount), l.currency, fmt(l.amount_ht), fmt(l.vat_amount), fmt(l.vat_rate), fmt(l.amount_ttc), fmt(l.amount_mad)].map(esc).join(";"));
    } else if (kind === "all") {
      const isCab = l.source === "cabinet";
      lines.push([
        l.invoice_date, l.source_label || (isCab ? "Cabinet" : "NDF"),
        isCab ? "" : (l.note_status || ""),
        isCab ? "" : (l.user_name || ""),
        l.vendor, l.invoice_number, l.category, l.description,
        fmt(l.amount), l.currency, fmt(l.amount_mad), l.mission_title || "",
      ].map(esc).join(";"));
    } else {
      lines.push([l.invoice_date, l.note_status, l.vendor, l.invoice_number, l.category, l.description, fmt(l.amount), l.currency, fmt(l.amount_mad), l.mission_title, l.user_name].map(esc).join(";"));
    }
  }
  return "\ufeff" + lines.join("\n");
}

export function downloadCsv(filename, csvText) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
